import { request } from "src/api/api-client";
import type { ChallengeDocument, ChallengeDocumentType } from "src/types/challenge-form-types";

export type DocumentServiceDocumentType = "FinanceContract" | "StatementOfAccount" | "DocumentFromDealer" | "Other";

// This needs more understanding of the Document Service to be implemented correctly,
// for now we are mapping all document types to "Other" except finance contract statements
function mapDocumentType(type: ChallengeDocumentType): DocumentServiceDocumentType {
  switch (type) {
    case "financeContract":
      return "FinanceContract";
    case "statementOfAccount":
      return "StatementOfAccount";
    case "documentFromDealer":
      return "DocumentFromDealer";
    case "somethingElse":
      return "Other";
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unsupported document type: ${exhaustiveCheck}`);
    }
  }
}

function isSafeDocumentNameChar(char: string): boolean {
  const code = char.charCodeAt(0);

  return (
    (code >= 48 && code <= 57) || // 0-9
    (code >= 65 && code <= 90) || // A-Z
    (code >= 97 && code <= 122) || // a-z
    char === "." ||
    char === "_" ||
    char === "-"
  );
}

function trimDocumentNameSeparators(value: string): string {
  let start = 0;
  let end = value.length;

  while (start < end && "_.-".includes(value[start])) {
    start += 1;
  }

  while (end > start && "_.-".includes(value[end - 1])) {
    end -= 1;
  }

  return value.slice(start, end);
}

const MAX_DOCUMENT_NAME_LENGTH = 200;
const PDF_EXTENSION = ".pdf";

function sanitizeDocumentName(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf(".");
  const rawBaseName = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;

  let safeBaseName = "";
  let previousCharacter = "";

  for (const originalCharacter of rawBaseName.normalize("NFKD")) {
    const character = originalCharacter === "×" ? "x" : originalCharacter;

    const nextCharacter = character.trim() === "" || !isSafeDocumentNameChar(character) ? "_" : character;

    if ((nextCharacter === "_" && previousCharacter === "_") || (nextCharacter === "-" && previousCharacter === "-")) {
      continue;
    }

    safeBaseName += nextCharacter;
    previousCharacter = nextCharacter;
  }

  const fallbackBaseName = trimDocumentNameSeparators(safeBaseName) || "document";
  const maxBaseNameLength = MAX_DOCUMENT_NAME_LENGTH - PDF_EXTENSION.length;

  const truncatedBaseName = trimDocumentNameSeparators(fallbackBaseName.slice(0, maxBaseNameLength)) || "document";

  return `${truncatedBaseName}${PDF_EXTENSION}`;
}

export async function uploadMfcDocument(document: ChallengeDocument): Promise<void> {
  if (!document.convertedPdf?.blob) {
    throw new Error(`Document "${document.file.name}" has not been converted to PDF`);
  }

  if (!document.documentType) {
    throw new Error(`Document "${document.file.name}" is missing a document type`);
  }

  const mappedDocumentType = mapDocumentType(document.documentType);
  const sanitizedDocumentName = sanitizeDocumentName(document.convertedPdf.fileName);

  const pdfFile = new File([document.convertedPdf.blob], sanitizedDocumentName, { type: "application/pdf" });

  const formData = new FormData();
  formData.append("file", pdfFile);
  formData.append("documentType", mappedDocumentType);
  formData.append("fileName", sanitizedDocumentName);

  const headers: Record<string, string> = {
    "x-document-name": sanitizedDocumentName,
    "x-document-type": mappedDocumentType,
  };

  await request<void, FormData>({
    url: import.meta.env.VITE_FCE_MFC_DOCUMENT_SERVICE_URL,
    method: "PUT",
    body: formData,
    headers: headers,
  });
}

export async function uploadMfcDocuments(documents: ChallengeDocument[]): Promise<void> {
  for (const document of documents) {
    await uploadMfcDocument(document);
  }
}
